import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { getLoggedInUser, getuserById } from "../../helpers/backend_helper";
import avatar from "../../assets/images/users/avatar-1.png";
import "./user-profile.scss";
import { useNavigate } from "react-router-dom";
import ChangeEntraPassword from "./ChangeEntraPassword";
import SecurityHistory from "./SecurityHistory";
import AccountDetails from "./AccountDetails";
import EditProfile from "./EditProfile";

const firstValue = (...values) =>
  values.find(
    (value) => value !== undefined && value !== null && value !== ""
  ) || "-";

const formatDate = (value, fallback = "-") => {
  if (!value) return fallback;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const UserProfile = () => {
  const [account, setAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState("");
  const [loadError, setLoadError] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setLoadError("");
    getLoggedInUser()
      .then((currentUser) => {
        if (!active) return null;
        setAccount(currentUser);
        const userId = currentUser?.userId || currentUser?.id;
        return userId ? getuserById(userId) : null;
      })
      .then((response) => {
        if (!active) return;
        if (!response?.status || !response.data) throw new Error();
        setAccount((previous) => ({ ...previous, ...response.data }));
      })
      .catch(() => {
        if (active)
          setLoadError(
            "Unable to load complete account details. Please try again."
          );
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [loadAttempt]);

  const profile = account || {};
  const fullName = firstValue(
    profile.fullName,
    profile.name,
    profile.userName,
    "User"
  );
  const email = firstValue(
    profile.emailId,
    profile.email,
    profile.userLogin?.loginName,
    profile.loginName
  );
  const firm = firstValue(
    profile.firmDetail?.name,
    profile.firmName,
    profile.companyName,
    profile.company?.name,
    profile.firmId === 1 ? "System" : null
  );
  const role = firstValue(
    profile.role?.name,
    profile.roleName,
    profile.userRole?.name,
    profile.designation
  );
  const details = [
    ["Email Address", email, "bx-envelope", "blue"],
    [
      "Contact Number",
      firstValue(profile.mobileNo, profile.mobileNumber, profile.phoneNumber),
      "bx-phone",
      "green",
    ],
    ["Firm", firm, "bx-building-house", "purple"],
    ["Role", role, "bx-shield-quarter", "amber"],
    [
      "Branch",
      firstValue(
        profile.branchDetail?.name,
        profile.branchName,
        profile.locationName,
        profile.location?.name
      ),
      "bx-map-pin",
      "blue",
    ],
    [
      "Designation",
      firstValue(profile.designation, profile.role?.name),
      "bx-id-card",
      "rose",
    ],
  ];

  return (
    <div className="page-content profile-page piers-page">
      <Container fluid>
        <header className="profile-page__header piers-page-header">
          <h1 className="piers-page-title">My Profile</h1>
          <p>View and manage your account information and access details.</p>
        </header>

        <Card className="profile-page__summary">
          <CardBody>
            <div className="profile-page__summary-main">
              <div className="profile-page__avatar-wrap">
                <img
                  src={avatar}
                  className="profile-page__avatar"
                  alt={`${fullName} profile`}
                />
              </div>
              <div className="profile-page__identity">
                <h2>{fullName}</h2>
                <p>{email}</p>
                <span className="profile-page__role">
                  <i className="bx bx-shield-quarter" />
                  {role}
                </span>
                <div className="profile-page__meta">
                  <span>
                    <i className="bx bx-calendar" />
                    Member since&nbsp;{" "}
                    {formatDate(
                      firstValue(
                        profile.createdDate,
                        profile.createdOn,
                        profile.dateCreated
                      ),
                      "-"
                    )}
                  </span>
                  <b>•</b>
                  <span>
                    <i className="bx bx-time-five" />
                    Last login&nbsp;{" "}
                    {formatDate(
                      firstValue(profile.lastLogin, profile.lastLoginDate),
                      "-"
                    )}
                  </span>
                </div>
              </div>
            </div>
            <button className="btn profile-page__outline-button" type="button" disabled={isLoading || Boolean(loadError) || !account} onClick={() => { setSaveSuccess(""); setEditOpen(true); }}>
              <i className="bx bx-pencil" />
              Edit Profile
            </button>
            <div className="profile-page__summary-art" aria-hidden="true">
              <i className="bx bx-shield-quarter" />
            </div>
          </CardBody>
        </Card>

        <Card className="profile-page__details">
          <CardBody>
            <div className="profile-page__section-heading">
              <span>
                <i className="bx bx-id-card" />
                Account Details
              </span>
              <button
                className="btn profile-page__outline-button profile-page__view-details"
                type="button"
                onClick={() => setDetailsOpen(true)}
                aria-haspopup="dialog"
              >
                <i className="bx bx-show" aria-hidden="true" />
                <span>View All Details</span>
              </button>
            </div>
            {isLoading && (
              <div className="profile-page__loading">Loading profile...</div>
            )}
            <Row className="g-0 profile-page__detail-grid">
              {details.map(([label, value, icon, tone]) => (
                <Col lg={4} md={6} key={label}>
                  <div className="profile-page__detail">
                    <i
                      className={`bx ${icon} profile-page__detail-icon profile-page__detail-icon--${tone}`}
                    />
                    <div>
                      <small>{label}</small>
                      <strong>{value}</strong>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </CardBody>
        </Card>

        <Row className="g-3 profile-page__bottom-panels">
          <Col lg={6}>
            <Card className="profile-page__panel h-100">
              <CardBody>
                <div className="profile-page__panel-title">
                  <i className="bx bx-shield-quarter" />
                  Security
                </div>
                <ChangeEntraPassword />
                <div className="profile-page__panel-row">
                  <i className="bx bx-shield profile-page__detail-icon profile-page__detail-icon--green" />
                  <div>
                    <small>Two-Factor Authentication</small>
                    <strong>Enabled</strong>
                  </div>
                  <span
                    className="profile-page__toggle profile-page__toggle--on"
                    aria-label="Two-factor authentication enabled"
                  >
                    <i />
                  </span>
                </div>
                <SecurityHistory />
              </CardBody>
            </Card>
          </Col>
          <Col lg={6}>
            <Card className="profile-page__panel h-100">
              <CardBody>
                <div className="profile-page__panel-title">
                  <i className="bx bx-slider-alt" />
                  Preferences
                </div>
                <div className="profile-page__panel-row">
                  <i className="bx bx-globe profile-page__detail-icon profile-page__detail-icon--blue" />
                  <div>
                    <small>Language</small>
                    <strong>English (US)</strong>
                  </div>
                  <i className="bx bx-chevron-down profile-page__chevron" />
                </div>
                <div className="profile-page__panel-row">
                  <i className="bx bx-time-five profile-page__detail-icon profile-page__detail-icon--amber" />
                  <div>
                    <small>Time Zone</small>
                    <strong>(GMT+05:30) Asia/Kolkata</strong>
                  </div>
                  <i className="bx bx-chevron-down profile-page__chevron" />
                </div>
                <button
                  className="btn profile-page__link-button"
                  type="button"
                  onClick={() => navigate("/notifications/settings")}
                >
                  Manage Preferences <i className="bx bx-chevron-right" />
                </button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      {saveSuccess && <div className="alert alert-success mx-3" role="status">{saveSuccess}</div>}
      {editOpen && <EditProfile profile={profile} close={() => setEditOpen(false)} saved={(changes) => {
        setAccount((previous) => ({ ...previous, ...changes }));
        setEditOpen(false);
        setSaveSuccess("Your profile has been updated.");
      }} />}
      <AccountDetails
        open={detailsOpen}
        close={() => setDetailsOpen(false)}
        profile={profile}
        fullName={fullName}
        details={details}
        loading={isLoading}
        error={loadError}
        retry={() => setLoadAttempt((a) => a + 1)}
      />
    </div>
  );
};

export default UserProfile;
